// Guest queue Inc 4a — the minimal bar. Presentational: the route owns
// useGuestQueue (one subscription) and passes the payload in. Renders inside
// ViewerStage's video column, a slim strip over the program. Shows grid
// occupancy + the caller's own line position, and a bid affordance that opens
// Stripe Checkout (the webhook records the bid; a fresh bid runs reconcile).
// Hidden once the caller is on-grid (they're being promoted to publish).

import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { LiveServiceError, liveRoomService } from './liveRoomService';
import type { RoomQueue } from './useGuestQueue';

import type { LiveRoomDetail } from './roomQueries';

const PRESETS_CENTS = [100, 500, 2000, 10000]; // $1 · $5 · $20 · $100

function bidError(err: unknown): string {
  if (err instanceof LiveServiceError) {
    if (err.code === 'queue_disabled') return 'The queue is closed for this room.';
    if (err.code === 'unauthenticated') return 'Sign in to bid.';
    if (err.code === 'room_not_found') return 'This room is no longer available.';
  }
  return 'Could not start checkout. Please try again.';
}

export function GuestQueueBar({ room, queue }: { room: LiveRoomDetail; queue?: RoomQueue }) {
  const qc = useQueryClient();
  const [pending, setPending] = useState(false);

  // After the Stripe return (?bid=1), refetch once so the just-recorded bid
  // reflects before the next Realtime event arrives.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (new URLSearchParams(window.location.search).get('bid') === '1') {
      void qc.invalidateQueries({ queryKey: ['room-queue', room.id] });
    }
  }, [qc, room.id]);

  // Hidden unless the queue is on and the caller isn't already on-grid.
  if (!room.queueEnabled || queue?.yourStatus === 'on_grid') return null;

  const bid = async (cents: number) => {
    setPending(true);
    try {
      const { url } = await liveRoomService.bidForGrid(room.livekitRoomName, cents);
      window.location.assign(url);
    } catch (err) {
      toast.error(bidError(err));
      setPending(false);
    }
  };

  return (
    <div className="pointer-events-auto flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-video-line bg-video-bg/85 px-4 py-2 font-sans text-xs text-video-text backdrop-blur">
      <span className="font-medium">
        Grid {queue ? queue.onGridCount : '—'}/{queue?.gridSlots ?? 8}
      </span>
      {queue?.yourStatus === 'waiting' ? (
        <span className="text-video-text-muted">
          You’re #{queue.yourRank} of {queue.waitingCount} in line — bid to move up
        </span>
      ) : (
        <span className="text-video-text-muted">Bid to join the grid and go on camera</span>
      )}
      <div className="ml-auto flex items-center gap-1.5">
        {PRESETS_CENTS.map((c) => (
          <button
            key={c}
            type="button"
            disabled={pending}
            onClick={() => void bid(c)}
            className="rounded-md bg-video-text px-2.5 py-1 font-medium text-video-bg transition hover:opacity-90 disabled:opacity-50"
          >
            ${c / 100}
          </button>
        ))}
      </div>
    </div>
  );
}
